import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SafetyBadge } from "@/components/bits";
import { FileSearch, ShieldCheck, Clock, FlaskConical, ArrowRight, AlertTriangle, PlugZap } from "lucide-react";

function Stat({ testid, label, value, Icon, tone }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5 animate-fade-up">
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</span>
        <Icon className={`h-5 w-5 ${tone}`} />
      </div>
      <div className="mt-3 font-serif text-4xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user, wp } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    client.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const canManage = user?.permissions?.manage;
  const liveBlocked = wp?.connected && wp?.read_only;
  const notConnected = wp && wp.connected === false;

  return (
    <div className="max-w-6xl">
      <div className="mb-8">
        <p className="text-xs font-mono uppercase tracking-widest text-blue-700">Editorial Command Center</p>
        <h1 className="font-serif text-3xl md:text-4xl font-bold text-slate-900 mt-1">Welcome back, {user?.name?.split(" ")[0]}</h1>
        <p className="text-slate-500 mt-2">Research a topic, verify what changed, and safely patch EntranceZone posts.</p>
      </div>

      {notConnected && (
        <Link to="/wordpress" data-testid="dashboard-wp-disconnected-banner" className="mb-6 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 hover:border-slate-300 transition-colors">
          <PlugZap className="h-5 w-5 text-slate-500 shrink-0" />
          <div className="text-sm text-slate-600"><b className="text-slate-800">WordPress not connected.</b> Connect a site to start researching and reviewing posts.</div>
        </Link>
      )}
      {liveBlocked && (
        <div data-testid="dashboard-live-blocked-banner" className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <b>LIVE publishing is unavailable.</b> WordPress isn't accepting authenticated writes on <span className="font-mono">{wp?.url}</span>, so the app stays in read-only / Dry Run.
            {canManage ? <> Run <Link to="/settings" className="underline font-semibold">Connection Diagnostics</Link> to see exactly what to fix.</> : <> Ask an admin to run Connection Diagnostics in Settings.</>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">
        <Stat testid="stat-scanned-topics" label="Topics Researched" value={stats?.scanned_topics ?? "—"} Icon={FileSearch} tone="text-blue-600" />
        <Stat testid="stat-verified-patches" label="Published + Verified" value={stats?.verified_patches ?? "—"} Icon={ShieldCheck} tone="text-emerald-600" />
        <Stat testid="stat-pending-approvals" label="Pending Approvals" value={stats?.pending_approvals ?? "—"} Icon={Clock} tone="text-amber-600" />
        <Stat testid="stat-dry-run-simulations" label="Dry-Run Simulations" value={stats?.dry_run_simulations ?? "—"} Icon={FlaskConical} tone="text-indigo-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Link to="/research" className="lg:col-span-2 rounded-xl border border-slate-200 bg-slate-900 text-white p-6 hover:bg-slate-800 transition-colors group">
          <p className="text-xs font-mono uppercase tracking-widest text-blue-300">Start here</p>
          <h2 className="font-serif text-2xl font-bold mt-2">Run AI Research</h2>
          <p className="text-slate-300 mt-2 text-sm max-w-md">Try the MVP case: <span className="font-mono text-white">JEE Main 2027</span>. We gather authoritative evidence and surface candidate posts.</p>
          <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-blue-300 group-hover:gap-3 transition-all">Open Research <ArrowRight className="h-4 w-4" /></span>
        </Link>

        <div data-testid="content-freshness-advisory-card" className="rounded-xl border border-slate-200 bg-white p-6">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Recent Proposals</p>
          <div className="mt-3 space-y-3">
            {(stats?.recent_proposals || []).slice(0, 4).map((p) => (
              <Link to="/review" key={p.id} className="block rounded-lg border border-slate-100 p-3 hover:border-slate-300">
                <div className="text-sm font-semibold text-slate-800 truncate">{p.post_title || p.query}</div>
                <div className="mt-1.5"><SafetyBadge status={p.status} /></div>
              </Link>
            ))}
            {(!stats?.recent_proposals || stats.recent_proposals.length === 0) && (
              <p className="text-sm text-slate-400">No proposals yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
